import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Megaphone, PenLine, RefreshCw } from 'lucide-react';
import { axios, auth, API, mbUrl, errMsg, NO_PERM, MbContext, CONN_LABEL, ORIGIN_CLS, fmtMyt, isOverdue, muted, primary, card, toast, store } from './mbApi';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConnectionCard } from './ConnectionCard';
import { OwnersCard } from './OwnersCard';
import { StatsRow } from './StatsRow';
import { WorkflowCard } from './WorkflowCard';
import { JoinsCard } from './JoinsCard';
import { Shortcuts } from './Shortcuts';
import { AnalystOverview } from './AnalystOverview';
import { AnalystConfigCard } from './AnalystConfigCard';
import { TaskDrawer } from './TaskDrawer';
import { TaskFilters, NO_FILTER, applyFilters } from './TaskFilters';
import { BriefDrawer } from './BriefDrawer';
import { GuideDrawer, GuideButton, Onboarding } from './GuideDrawer';
import { DangerZone } from './DangerZone';
import { CompareTable } from './CompareTable';

const VIEWS = ['mine', 'all', 'results', 'settings'];
const TASK_KEYS = ['mb_task', 'mb_sec', 'mb_ver'];

const LegacyCard = ({ dash }) => (
  <div className={`${card} text-sm ${muted}`} data-testid="mb-legacy-card">
    <p className={`text-base font-semibold ${primary} mb-2`}>Modul sedia ada (read-only reference)</p>
    <p>Creative Requests: {dash.legacy.creative_requests.total} total · {dash.legacy.creative_requests.completed} completed</p>
    <p>Project Tasks (semua department): {dash.legacy.project_tasks.total} total · {dash.legacy.project_tasks.open} open</p>
    <p>Telegram: {dash.legacy.telegram.linked ? dash.legacy.telegram.channel || 'linked' : 'belum link'}</p>
    <p className="mt-2 italic">{dash.notes}</p>
  </div>
);

const StatusStrip = ({ settings, dash, mcpInfo }) => (
  <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${muted} rounded-lg border border-[hsl(var(--border))] px-3 py-2`} data-testid="mb-status-strip">
    <span>Meta: <span className={primary}>{CONN_LABEL[settings.connection_status] || settings.connection_status}</span>{mcpInfo.mode === 'mock' ? ' (mock)' : ''}</span>
    <span>Last sync: <span className={primary}>{fmtMyt(settings.last_sync_at)}</span></span>
    <span>Auto-sync: <span className={primary}>{settings.auto_sync_enabled ? 'ON' : 'OFF'}</span></span>
    <span>Coverage hari ini: <span className={primary}>{dash?.coverage?.today?.pct != null ? `${dash.coverage.today.pct}%` : '—'}</span></span>
    <span>CPS semua join hari ini: <span className={primary}>{dash?.actual?.cps_myr != null ? `RM ${dash.actual.cps_myr.toFixed(2)}` : 'N/A'}</span></span>
    {dash?.provenance && <span className={ORIGIN_CLS[dash.provenance.origin]} data-testid="mb-strip-origin">{dash.provenance.label}</span>}
  </div>
);

const DemoBanner = () => (
  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300" data-testid="mb-demo-banner">
    <span className="font-semibold">MOD DEMO — DATA CONTOH.</span> Semua angka, iklan, link invite dan cadangan AI di sini ialah contoh latihan.
    Tiada panggilan ke Meta, Telegram atau AI sebenar, dan tiada kerja dihantar kepada team.
  </div>
);

const LearningsList = ({ learnings, creatives, openTask }) => (
  <div className={card} data-testid="mb-learnings-list">
    <p className={`text-base font-semibold ${primary} mb-2`}>Rekod Pembelajaran ({learnings.length})</p>
    {learnings.length === 0 && <p className={`text-sm ${muted}`}>Belum ada. Rekod dari setiap versi bahan selepas ujian (butiran task → Pembelajaran).</p>}
    {learnings.slice(0, 30).map((l) => {
      const c = creatives.find((x) => x.id === l.creative_id);
      return (
        <p key={l.id} className={`text-sm ${muted}`}>
          <span className={primary}>{l.version_label}</span> · {{ menang: 'Menang', kalah: 'Kalah', tak_pasti: 'Tak pasti' }[l.result]} · {l.insight}
          {l.next_action ? ` → ${l.next_action}` : ''} <span className="opacity-80">({l.author_name}, {new Date(l.created_at).toLocaleDateString('en-GB')})</span>
          {c && <button className="ml-2 underline text-[hsl(var(--primary))] min-h-8" onClick={() => openTask(c.workflow_id, 'belajar', c.id)} data-testid={`mb-learning-open-${l.id}`}>Buka</button>}
        </p>
      );
    })}
  </div>
);

const ListSkeleton = () => (
  <div className="space-y-2" data-testid="mb-board-loading">{[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-lg bg-white/[0.04] animate-pulse" />)}</div>
);

const BoardError = ({ msg, retry }) => (
  <div className="text-sm text-red-400 space-y-2" data-testid="mb-board-error">
    <p>{msg}</p>
    <Button variant="outline" className="h-10" onClick={retry} data-testid="mb-board-retry"><RefreshCw size={14} className="mr-1.5" /> Cuba lagi</Button>
  </div>
);

const byUrgency = (a, b) => (Number(isOverdue(b)) - Number(isOverdue(a))) || (a.deadline || '9999').localeCompare(b.deadline || '9999');

const useTaskParam = () => {
  const [params, setParams] = useSearchParams();
  const open = useCallback((id, sec = 'ringkasan', ver = '', replace = false) => setParams((p) => {
    const n = new URLSearchParams(p);
    TASK_KEYS.forEach((k) => n.delete(k));
    if (id) {
      n.set('tab', 'media-buying');
      n.set('mb_task', id);
      n.set('mb_sec', sec);
      if (ver) n.set('mb_ver', ver);
    }
    return n;
  }, { replace }), [setParams]);
  return { taskId: params.get('mb_task') || '', sec: params.get('mb_sec') || 'ringkasan', ver: params.get('mb_ver') || '', open };
};

const useBoard = (ibId, ibName) => {
  const [board, setBoard] = useState({ workflows: [], creatives: [], tests: [], learnings: [] });
  const [dash, setDash] = useState(null);
  const [state, setState] = useState({ loaded: false, error: '' });
  const load = useCallback(async () => {
    try {
      const [b, d] = await Promise.all([
        axios.get(mbUrl(ibId, '/board'), auth()),
        axios.get(mbUrl(ibId, '/dashboard'), { ...auth(), params: { ib_name: ibName || '' } }),
      ]);
      setBoard(b.data);
      setDash(d.data);
      setState({ loaded: true, error: '' });
    } catch (e) {
      setState((s) => ({ ...s, error: s.loaded ? '' : errMsg(e, 'Gagal load papan kerja') }));
      if (e.response?.status !== 403) toast.error(errMsg(e, 'Gagal load papan kerja'));
    }
  }, [ibId, ibName]);
  return { board, dash, ...state, load };
};

export const MediaBuyingContent = ({ ib }) => {
  const ibId = ib?.id;
  const [info, setInfo] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [view, setViewRaw] = useState(() => (VIEWS.includes(store.get('mb-view')) ? store.get('mb-view') : 'mine'));
  const [members, setMembers] = useState([]);
  const [analyst, setAnalyst] = useState(null);
  const [analystErr, setAnalystErr] = useState('');
  const [draft, setDraft] = useState(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [filters, setFilters] = useState(NO_FILTER);
  const task = useTaskParam();
  const openTask = task.open;
  const { board, dash, loaded, error: boardErr, load: loadBoard } = useBoard(ibId, ib?.name);
  const setView = useCallback((v) => { store.set('mb-view', v); setViewRaw(v); }, []);
  useEffect(() => { axios.get(`${API}/team-members`, auth()).then((r) => setMembers(r.data.members || [])).catch(() => {}); }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(mbUrl(ibId, '/settings'), auth());
      setInfo(data);
      setLoadError('');
      return data;
    } catch (e) {
      setLoadError(e.response?.status === 403 ? 'noaccess' : errMsg(e, 'Gagal load Marketing Hub'));
      return null;
    }
  }, [ibId]);

  const enabled = info?.settings?.enabled;
  useEffect(() => { if (ibId) loadSettings(); }, [ibId, loadSettings]);
  useEffect(() => { if (enabled) loadBoard(); }, [enabled, loadBoard]);
  const loadAnalyst = useCallback(async () => {
    setAnalystErr('');
    try { setAnalyst((await axios.get(mbUrl(ibId, '/analyst/config'), auth())).data); } catch (e) { setAnalystErr(errMsg(e, 'Gagal load tetapan analisis')); }
  }, [ibId]);
  useEffect(() => { if (enabled) loadAnalyst(); }, [enabled, loadAnalyst]);
  const openDraft = useCallback((sg) => {
    setDraft({ suggestionId: sg.id, label: sg.title, from: sg.version_label, fields: sg.brief_draft || {}, branch: sg.production_path });
    openTask('', '', '', true);
    setBriefOpen(true);
  }, [openTask]);
  const ctx = useMemo(() => ({
    accounts: info?.settings?.ad_account_ids || [], owners: info?.settings?.owners || {}, members, analyst, openDraft, openTask, workflows: board.workflows,
  }), [info, members, analyst, openDraft, openTask, board.workflows]);

  const reloadAll = async () => { if ((await loadSettings())?.settings?.enabled) loadBoard(); };
  const simulate = (info?.telegram?.mode === 'mock' || info?.settings?.is_demo) ? async (t) => {
    try { await axios.post(mbUrl(ibId, '/telegram/mock-join'), { test_id: t.id }, auth()); toast.success(info?.settings?.is_demo ? 'Join contoh (demo) direkod' : 'Mock join recorded'); loadBoard(); } catch (e) { toast.error(errMsg(e)); }
  } : null;

  if (loadError === 'noaccess') {
    return <div className={`m-6 ${card} text-sm ${muted}`} data-testid="mb-no-access">Awak tiada akses kepada channel ni dalam Marketing Hub. Minta admin tambah channel ni dalam Channel Access awak.</div>;
  }
  if (loadError) {
    return (
      <div className={`m-6 ${card} text-sm text-red-400 space-y-2`} data-testid="mb-load-error">
        <p>{loadError}</p>
        <Button variant="outline" className="h-10" onClick={loadSettings} data-testid="mb-retry-btn">Cuba lagi</Button>
      </div>
    );
  }
  if (!info) return <div className="mb-scope p-4"><ListSkeleton /></div>;
  const { settings, can_manage: canManage, mcp: mcpInfo } = info;
  const caps = info.capabilities || {};
  const isAdmin = !!info.me?.is_admin;
  const myId = info.me?.id;
  const tab = view === 'settings' && !canManage ? 'mine' : view;
  const mine = board.workflows.filter((w) => !w.archived && w.current_pic_id === myId).sort(byUrgency);
  const visible = applyFilters(board.workflows.filter((w) => showArchived || !w.archived), filters);
  const archivedCount = [board.workflows, board.creatives, board.tests].reduce((n, rows) => n + rows.filter((r) => r.archived).length, 0);
  const openW = board.workflows.find((w) => w.id === task.taskId) || null;
  const renderCard = (w) => <WorkflowCard key={w.id} w={w} board={board} onOpen={(id, sec) => task.open(id, sec)} />;
  const listBody = (rows, empty) => {
    if (boardErr) return <BoardError msg={boardErr} retry={loadBoard} />;
    if (!loaded) return <ListSkeleton />;
    return rows.length === 0 ? empty : rows.map(renderCard);
  };
  const openBrief = () => { setView('all'); setBriefOpen(true); };

  return (
    <MbContext.Provider value={ctx}>
    <div className="mb-scope px-4 sm:px-8 py-5 space-y-5" data-testid="media-buying-content">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className={`text-xl lg:text-2xl font-bold ${primary} flex items-center gap-2`}>
            <Megaphone size={20} className="text-[hsl(var(--primary))]" /> Marketing Hub
          </h2>
          <p className={`text-sm ${muted}`}>Brief → bahan & versi → review → ujian + invite link → result, dengan data Meta Ads (read-only)</p>
        </div>
        <GuideButton onClick={() => setGuideOpen(true)} />
      </div>

      {settings?.is_demo && <DemoBanner />}
      <Onboarding onOpenGuide={() => setGuideOpen(true)} />

      {!settings.enabled ? (
        <>
          <ConnectionCard ibId={ibId} settings={settings} mcpInfo={mcpInfo} telegramMode={info.telegram?.mode} canManage={canManage} isAdmin={isAdmin} onChanged={reloadAll} />
          <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-6 text-center" data-testid="mb-disabled-state">
            <p className={`text-sm ${muted}`}>Marketing Hub belum dihidupkan untuk channel ni. Data sedia ada tak terusik.</p>
          </div>
        </>
      ) : (
        <>
          <StatusStrip settings={settings} dash={dash} mcpInfo={mcpInfo} />
          <Shortcuts ibId={ibId} caps={caps} mineCount={mine.length} setView={setView} onBrief={openBrief} onChanged={loadBoard} />
          <Tabs value={tab} onValueChange={setView} data-testid="mb-view-tabs">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="mine" className="min-h-10" data-testid="mb-tab-mine">Tugasan Saya ({mine.length})</TabsTrigger>
              <TabsTrigger value="all" className="min-h-10" data-testid="mb-tab-all">Semua Tugasan</TabsTrigger>
              <TabsTrigger value="results" className="min-h-10" data-testid="mb-tab-results">Hasil</TabsTrigger>
              {canManage && <TabsTrigger value="settings" className="min-h-10" data-testid="mb-tab-settings">Tetapan</TabsTrigger>}
            </TabsList>
            <TabsContent value="mine" className="space-y-2 mt-4" data-testid="mb-mine-list">
              <p className={`text-sm ${muted}`}>Task yang PIC semasanya awak — lewat deadline di atas. Tekan butang pada kad untuk terus ke tempat kerja.</p>
              {listBody(mine, (
                <p className={`text-sm ${muted}`} data-testid="mb-mine-empty">
                  Tiada tugasan di tangan awak sekarang. Task muncul di sini bila diserah kepada awak sebagai PIC. Kalau rasa sepatutnya ada,
                  semak <button className="underline min-h-8" onClick={() => setView('all')} data-testid="mb-mine-empty-all">Semua Tugasan</button> atau minta manager semak PIC dalam Owners.
                </p>
              ))}
            </TabsContent>
            <TabsContent value="all" className="mt-4">
              <div className={`${card} space-y-3`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className={`text-base font-semibold ${primary} flex items-center gap-2`}><Target size={16} /> Papan Kerja Marketing</p>
                  {caps.brief
                    ? <Button className="h-10" onClick={() => setBriefOpen(true)} data-testid="mb-open-brief"><PenLine size={15} className="mr-1.5" /> Buat Brief</Button>
                    : <p className={`text-sm ${muted}`} data-testid="mb-brief-noperm">Buat brief: {NO_PERM.brief}</p>}
                </div>
                <TaskFilters f={filters} setF={setFilters} members={members} />
                <div className={`flex flex-wrap items-center justify-between gap-2 text-sm ${muted}`}>
                  <span data-testid="mb-filter-count">{loaded ? `${visible.length} task` : ''}</span>
                  {archivedCount > 0 && (
                    <button className="underline min-h-10" onClick={() => setShowArchived(!showArchived)} data-testid="mb-show-archived">
                      {showArchived ? 'Sembunyi arkib' : `Tunjuk arkib (${archivedCount})`}
                    </button>
                  )}
                </div>
                <div className="space-y-2" data-testid="mb-workflow-list">
                  {listBody(visible, <p className={`text-sm ${muted}`}>{board.workflows.length ? 'Tiada task padan dengan tapisan.' : 'Belum ada brief — tekan Buat Brief untuk mula.'}</p>)}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="results" className="space-y-5 mt-4">
              <StatsRow dash={dash} />
              <CompareTable ibId={ibId} members={members} />
              <JoinsCard ibId={ibId} dash={dash} telegramMode={info.telegram?.mode} canManage={canManage} onChanged={loadBoard} />
              <AnalystOverview ibId={ibId} canBrief={!!caps.brief} />
              <LearningsList learnings={board.learnings || []} creatives={board.creatives} openTask={task.open} />
              {dash?.legacy && <LegacyCard dash={dash} />}
            </TabsContent>
            {canManage && (
              <TabsContent value="settings" className="space-y-5 mt-4">
                <ConnectionCard ibId={ibId} settings={settings} mcpInfo={mcpInfo} telegramMode={info.telegram?.mode} canManage={canManage} isAdmin={isAdmin} onChanged={reloadAll} />
                <OwnersCard ibId={ibId} owners={settings.owners} canManage={canManage} onSaved={loadSettings} />
                <AnalystConfigCard ibId={ibId} analyst={analyst} error={analystErr} onRetry={loadAnalyst} onSaved={loadAnalyst} />
                <DangerZone ibId={ibId} isAdmin={isAdmin} onChanged={reloadAll} />
              </TabsContent>
            )}
          </Tabs>
          {!canManage && <OwnersCard ibId={ibId} owners={settings.owners} canManage={false} onSaved={loadSettings} />}
          <TaskDrawer w={openW} tab={task.sec} setTab={(s) => task.open(task.taskId, s, '', true)} focusId={task.ver} onClose={() => task.open('', '', '', true)}
            ibId={ibId} board={board} dash={dash} caps={caps} showArchived={showArchived} reload={loadBoard} simulate={simulate} />
          <BriefDrawer open={briefOpen} setOpen={setBriefOpen} ibId={ibId} draft={draft} onDraftDone={() => setDraft(null)}
            onCreated={(w) => { loadBoard(); if (w?.id) task.open(w.id, 'ringkasan'); }} />
        </>
      )}
      <GuideDrawer open={guideOpen} setOpen={setGuideOpen} canManage={canManage} />
    </div>
    </MbContext.Provider>
  );
};
